/**
 * 课件相关工具函数
 */

/**
 * 解析课件时间节点，提取可用于进度条显示的信息
 * @param {Array} presentationEventData - 课件事件数据
 * @returns {Array} 包含时间节点信息的数组 [{time, type, presentationId, slideUrl, title}]
 */
export const parsePresentationTimeNodes = (presentationEventData) => {

    if (!presentationEventData || !Array.isArray(presentationEventData)) {
        return [];
    }

    const timeNodes = [];
    // 存储所有课件信息，用于补充258事件的slideUrl
    const allPresentations = new Map();

    // 第一遍遍历：收集所有课件信息（类型288）
    presentationEventData.forEach(event => {
        // 类型288: 课件列表事件
        if (event.type === 288 && event.data && event.data.presentations) {
            // 收集所有课件信息
            event.data.presentations.forEach(pres => {
                if (pres.presentationId && !allPresentations.has(pres.presentationId)) {
                    allPresentations.set(pres.presentationId, pres);
                }
            });
        }
    });

    // 第二遍遍历：处理时间节点（只考虑258消息）
    presentationEventData.forEach(event => {
        // 类型258: 课件切换事件
        if (event.type === 258 && event.data) {
            // 从所有课件信息中查找对应的slideUrl
            const presentationInfo = allPresentations.get(event.data.presentationId);
            timeNodes.push({
                time: event.milliseconds, // 时间点（秒，精确到毫秒的小数）
                type: 'presentation_switch', // 课件切换类型
                presentationId: event.data.presentationId,
                slideUrl: presentationInfo ? presentationInfo.slide : null, // 补充slide URL
                title: presentationInfo && presentationInfo.name ? presentationInfo.name : `切换到课件 ${event.data.presentationId}`
            });
        }
    });

    // 按时间排序
    const sortedNodes = timeNodes.sort((a, b) => a.time - b.time);
    
    // 过滤连续时间段内同一张课件的重复切换记录，仅保留第一次切换记录
    const filteredNodes = [];
    let lastPresentationId = null;
    
    for (let i = 0; i < sortedNodes.length; i++) {
        const currentNode = sortedNodes[i];
        
        // 如果是课件切换事件且与上一个记录是同一个课件，则跳过
        if (currentNode.type === 'presentation_switch' && currentNode.presentationId === lastPresentationId) {
            continue;
        }
        
        // 保留该记录
        filteredNodes.push(currentNode);
        
        // 更新最后的课件ID（仅对切换事件更新）
        if (currentNode.type === 'presentation_switch') {
            lastPresentationId = currentNode.presentationId;
        } else if (currentNode.type === 'presentation_list') {
            // 对于列表事件，重置lastPresentationId，因为这表示一个新的课件分享时段开始
            lastPresentationId = null;
        }
    }
    
    return filteredNodes;
};
/**
 * 过滤和处理课件时间节点，只保留有效的节点
 * @param {Array} timeNodes - 时间节点数组
 * @param {number} duration - 总时长（秒）
 * @returns {Array} 过滤后的时间节点数组
 */
export const filterPresentationTimeNodes = (timeNodes, duration) => {
    if (!timeNodes || !Array.isArray(timeNodes)) {
        return [];
    }

    // 过滤掉超出范围的节点，并去重
    const filteredNodes = [];
    const seenTimes = new Set();

    timeNodes.forEach(node => {
        // 检查时间是否在有效范围内
        // node.time 和 duration 都是以秒为单位
        if (node.time >= 0 && node.time <= duration) {
            // 去重：同一时间点只保留一个节点
            if (!seenTimes.has(node.time)) {
                seenTimes.add(node.time);
                filteredNodes.push(node);
            }
        }
    });

    return filteredNodes;
};

/**
 * 格式化时间节点显示标题
 * @param {Object} node - 时间节点对象
 * @returns {string} 格式化后的标题
 */
export const formatTimeNodeTitle = (node) => {
    if (!node) return '';

    // 格式化时间
    // node.time 是以秒为单位（精确到毫秒的小数）
    const totalSeconds = Math.floor(node.time);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    return `${node.title} (${timeStr})`;
};

/**
 * 获取课件时间节点的统计信息
 * @param {Array} timeNodes - 时间节点数组
 * @returns {Object} 统计信息
 */
export const getPresentationStats = (timeNodes) => {
    if (!timeNodes || !Array.isArray(timeNodes)) {
        return {
            totalNodes: 0,
            presentationListCount: 0,
            presentationSwitchCount: 0
        };
    }

    const stats = {
        totalNodes: timeNodes.length,
        presentationListCount: 0,
        presentationSwitchCount: 0
    };

    timeNodes.forEach(node => {
        if (node.type === 'presentation_list') {
            stats.presentationListCount++;
        } else if (node.type === 'presentation_switch') {
            stats.presentationSwitchCount++;
        }
    });

    return stats;
};

/**
 * 根据当前时间获取最近的课件时间节点
 * @param {Array} timeNodes - 时间节点数组
 * @param {number} currentTime - 当前时间（毫秒）
 * @returns {Object|null} 最近的时间节点
 */
export const getNearestPresentationNode = (timeNodes, currentTime) => {
    if (!timeNodes || !Array.isArray(timeNodes) || timeNodes.length === 0) {
        return null;
    }

    // 找到小于等于当前时间的最后一个节点
    // currentTime 是以毫秒为单位，需要转换为秒进行比较
    const currentTimeInSeconds = currentTime / 1000;
    for (let i = timeNodes.length - 1; i >= 0; i--) {
        if (timeNodes[i].time <= currentTimeInSeconds) {
            return timeNodes[i];
        }
    }

    // 如果没有找到，返回第一个节点
    return timeNodes[0];
};

/**
 * 获取下一个课件时间节点
 * @param {Array} timeNodes - 时间节点数组
 * @param {number} currentTime - 当前时间（毫秒）
 * @returns {Object|null} 下一个时间节点
 */
export const getNextPresentationNode = (timeNodes, currentTime) => {
    if (!timeNodes || !Array.isArray(timeNodes) || timeNodes.length === 0) {
        return null;
    }

    // 找到大于当前时间的第一个节点
    // currentTime 是以毫秒为单位，需要转换为秒进行比较
    const currentTimeInSeconds = currentTime / 1000;
    for (let i = 0; i < timeNodes.length; i++) {
        if (timeNodes[i].time > currentTimeInSeconds) {
            return timeNodes[i];
        }
    }

    return null;
};